import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
  AfterViewInit
} from '@angular/core';

export interface CropArea {
  x: number;      // X position as percentage (0-100)
  y: number;      // Y position as percentage (0-100)
  width: number;  // Width as percentage
  height: number; // Height as percentage
}

export interface ImageCropResult {
  cropArea: CropArea;
  croppedImageBase64?: string;
}

@Component({
  selector: 'app-image-crop-selector',
  standalone: false,
  templateUrl: './image-crop-selector.html',
  styleUrl: './image-crop-selector.css'
})
export class ImageCropSelector implements OnChanges, AfterViewInit {
  // Image source - can be a File, base64 string, or URL
  @Input() imageSource: File | string | null = null;

  // Aspect ratio of the crop area (width / height)
  // e.g., 16/9 for banner, 1 for square, 3/1 for wide header
  @Input() aspectRatio: number = 3; // Default wide banner ratio

  // Minimum crop area size as percentage of image
  @Input() minCropPercent: number = 20;

  // Initial crop position (optional)
  @Input() initialCrop: CropArea | null = null;

  // Whether to generate base64 output (can be slow for large images)
  @Input() generateBase64: boolean = false;

  // Output events
  @Output() cropChanged = new EventEmitter<ImageCropResult>();
  @Output() cropConfirmed = new EventEmitter<ImageCropResult>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('imageContainer') imageContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('sourceImage') sourceImage!: ElementRef<HTMLImageElement>;
  @ViewChild('cropBox') cropBox!: ElementRef<HTMLDivElement>;

  imageUrl: string = '';
  imageLoaded = false;

  // Image dimensions
  naturalWidth = 0;
  naturalHeight = 0;
  displayWidth = 0;
  displayHeight = 0;

  // Crop box position and size (in pixels relative to displayed image)
  cropX = 0;
  cropY = 0;
  cropWidth = 0;
  cropHeight = 0;

  // Dragging state
  isDragging = false;
  dragStartX = 0;
  dragStartY = 0;
  dragStartCropX = 0;
  dragStartCropY = 0;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['imageSource'] && this.imageSource) {
      this.loadImage();
    }
  }

  ngAfterViewInit() {
    if (this.imageSource) {
      this.loadImage();
    }
  }

  private loadImage() {
    if (!this.imageSource) return;

    if (this.imageSource instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageUrl = e.target?.result as string;
      };
      reader.readAsDataURL(this.imageSource);
    } else {
      this.imageUrl = this.imageSource;
    }
  }

  onImageLoad() {
    const img = this.sourceImage.nativeElement;
    this.naturalWidth = img.naturalWidth;
    this.naturalHeight = img.naturalHeight;
    this.displayWidth = img.offsetWidth;
    this.displayHeight = img.offsetHeight;
    this.imageLoaded = true;

    this.initializeCropBox();
  }

  private initializeCropBox() {
    if (this.initialCrop) {
      // Use initial crop position
      this.cropX = (this.initialCrop.x / 100) * this.displayWidth;
      this.cropY = (this.initialCrop.y / 100) * this.displayHeight;
      this.cropWidth = (this.initialCrop.width / 100) * this.displayWidth;
      this.cropHeight = (this.initialCrop.height / 100) * this.displayHeight;
    } else {
      // Calculate crop box size based on aspect ratio
      // Try to make crop box as large as possible while maintaining aspect ratio
      const imageAspect = this.displayWidth / this.displayHeight;

      if (this.aspectRatio > imageAspect) {
        // Crop box is wider than image aspect - fit to width
        this.cropWidth = this.displayWidth;
        this.cropHeight = this.cropWidth / this.aspectRatio;
      } else {
        // Crop box is taller than image aspect - fit to height
        this.cropHeight = this.displayHeight;
        this.cropWidth = this.cropHeight * this.aspectRatio;
      }

      // Center the crop box
      this.cropX = (this.displayWidth - this.cropWidth) / 2;
      this.cropY = (this.displayHeight - this.cropHeight) / 2;
    }

    this.emitCropChange();
  }

  // Mouse/Touch event handlers
  onMouseDown(event: MouseEvent) {
    event.preventDefault();
    this.startDrag(event.clientX, event.clientY);
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 1) {
      event.preventDefault();
      const touch = event.touches[0];
      this.startDrag(touch.clientX, touch.clientY);
    }
  }

  private startDrag(clientX: number, clientY: number) {
    this.isDragging = true;
    this.dragStartX = clientX;
    this.dragStartY = clientY;
    this.dragStartCropX = this.cropX;
    this.dragStartCropY = this.cropY;

    // Add global listeners
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('touchmove', this.onTouchMove);
    document.addEventListener('touchend', this.onTouchEnd);
  }

  onMouseMove = (event: MouseEvent) => {
    if (!this.isDragging) return;
    this.updateDrag(event.clientX, event.clientY);
  };

  onTouchMove = (event: TouchEvent) => {
    if (!this.isDragging || event.touches.length !== 1) return;
    const touch = event.touches[0];
    this.updateDrag(touch.clientX, touch.clientY);
  };

  private updateDrag(clientX: number, clientY: number) {
    const deltaX = clientX - this.dragStartX;
    const deltaY = clientY - this.dragStartY;

    let newX = this.dragStartCropX + deltaX;
    let newY = this.dragStartCropY + deltaY;

    // Constrain to image bounds
    newX = Math.max(0, Math.min(newX, this.displayWidth - this.cropWidth));
    newY = Math.max(0, Math.min(newY, this.displayHeight - this.cropHeight));

    this.cropX = newX;
    this.cropY = newY;

    this.emitCropChange();
  }

  onMouseUp = () => {
    this.endDrag();
  };

  onTouchEnd = () => {
    this.endDrag();
  };

  private endDrag() {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('touchmove', this.onTouchMove);
    document.removeEventListener('touchend', this.onTouchEnd);
  }

  private getCropArea(): CropArea {
    return {
      x: (this.cropX / this.displayWidth) * 100,
      y: (this.cropY / this.displayHeight) * 100,
      width: (this.cropWidth / this.displayWidth) * 100,
      height: (this.cropHeight / this.displayHeight) * 100
    };
  }

  private emitCropChange() {
    const result: ImageCropResult = {
      cropArea: this.getCropArea()
    };
    this.cropChanged.emit(result);
  }

  confirm() {
    const cropArea = this.getCropArea();
    const result: ImageCropResult = { cropArea };

    if (this.generateBase64) {
      result.croppedImageBase64 = this.generateCroppedImage();
    }

    this.cropConfirmed.emit(result);
  }

  cancel() {
    this.cancelled.emit();
  }

  private generateCroppedImage(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    const img = this.sourceImage.nativeElement;
    const scaleX = this.naturalWidth / this.displayWidth;
    const scaleY = this.naturalHeight / this.displayHeight;

    // Calculate source coordinates in natural image size
    const sx = this.cropX * scaleX;
    const sy = this.cropY * scaleY;
    const sw = this.cropWidth * scaleX;
    const sh = this.cropHeight * scaleY;

    // Set canvas size to crop size (or scaled down if too large)
    const maxOutputSize = 1920;
    let outputWidth = sw;
    let outputHeight = sh;

    if (outputWidth > maxOutputSize || outputHeight > maxOutputSize) {
      const scale = maxOutputSize / Math.max(outputWidth, outputHeight);
      outputWidth *= scale;
      outputHeight *= scale;
    }

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outputWidth, outputHeight);

    return canvas.toDataURL('image/jpeg', 0.9);
  }

  // Get CSS transform for the crop overlay
  get overlayClipPath(): string {
    // Create a clip path that shows everything except the crop area
    const left = this.cropX;
    const top = this.cropY;
    const right = this.cropX + this.cropWidth;
    const bottom = this.cropY + this.cropHeight;

    return `polygon(
      0% 0%, 0% 100%, ${left}px 100%, ${left}px ${top}px,
      ${right}px ${top}px, ${right}px ${bottom}px,
      ${left}px ${bottom}px, ${left}px 100%, 100% 100%, 100% 0%
    )`;
  }

  ngOnDestroy() {
    // Clean up any remaining listeners
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('touchmove', this.onTouchMove);
    document.removeEventListener('touchend', this.onTouchEnd);
  }
}
